// src/enums.ts
var UserRole = {
  ADMIN: "ADMIN",
  RECEPTIONIST: "RECEPTIONIST",
  DOCTOR: "DOCTOR",
  DISPLAY: "DISPLAY"
};
var USER_ROLES = Object.values(UserRole);
var TokenStatus = {
  WAITING: "WAITING",
  CALLED: "CALLED",
  IN_CONSULTATION: "IN_CONSULTATION",
  COMPLETED: "COMPLETED",
  SKIPPED: "SKIPPED",
  CANCELLED: "CANCELLED",
  NO_SHOW: "NO_SHOW"
};
var TOKEN_STATUSES = Object.values(TokenStatus);
var AppointmentStatus = {
  SCHEDULED: "SCHEDULED",
  CHECKED_IN: "CHECKED_IN",
  WAITING: "WAITING",
  CALLED: "CALLED",
  IN_CONSULTATION: "IN_CONSULTATION",
  COMPLETED: "COMPLETED",
  CANCELLED: "CANCELLED",
  NO_SHOW: "NO_SHOW",
  SKIPPED: "SKIPPED"
};
var APPOINTMENT_STATUSES = Object.values(AppointmentStatus);
var AppointmentType = {
  WALK_IN: "WALK_IN",
  SCHEDULED: "SCHEDULED",
  FOLLOW_UP: "FOLLOW_UP",
  EMERGENCY: "EMERGENCY"
};
var APPOINTMENT_TYPES = Object.values(AppointmentType);
var QueueStatus = {
  OPEN: "OPEN",
  PAUSED: "PAUSED",
  CLOSED: "CLOSED"
};
var QUEUE_STATUSES = Object.values(QueueStatus);
var Gender = {
  MALE: "MALE",
  FEMALE: "FEMALE",
  OTHER: "OTHER"
};
var GENDERS = Object.values(Gender);
var EntityStatus = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE"
};
var PatientStatus = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  BLOCKED: "BLOCKED"
};
var UserStatus = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  LOCKED: "LOCKED"
};
var ChamberStatus = {
  ACTIVE: "ACTIVE",
  INACTIVE: "INACTIVE",
  MAINTENANCE: "MAINTENANCE"
};
var CallNextPolicy = {
  /** Settle the active token automatically, then call the next one. */
  AUTO_COMPLETE: "AUTO_COMPLETE",
  /** Refuse to call next while a token is CALLED or IN_CONSULTATION. */
  STRICT: "STRICT"
};
var NotificationChannel = {
  IN_APP: "IN_APP",
  SMS: "SMS",
  WHATSAPP: "WHATSAPP",
  EMAIL: "EMAIL",
  PUSH: "PUSH"
};
var NotificationStatus = {
  PENDING: "PENDING",
  SENT: "SENT",
  FAILED: "FAILED",
  SKIPPED: "SKIPPED"
};
var EMERGENCY_PRIORITY = 10;
var NORMAL_PRIORITY = 0;
var ConsultationStatus = {
  /** The doctor is still dictating / editing. Only they can see it. */
  DRAFT: "DRAFT",
  /** Signed off. Immutable — a correction is a new amendment, not an edit. */
  SIGNED: "SIGNED"
};
var CONSULTATION_STATUSES = Object.values(ConsultationStatus);
var RxSource = {
  DICTATION: "DICTATION",
  MANUAL: "MANUAL"
};
var DictationMode = {
  DICTATION: "DICTATION",
  CONVERSATION: "CONVERSATION"
};
var DICTATION_MODES = Object.values(DictationMode);
var AuditAction = {
  PATIENT_CREATED: "PATIENT_CREATED",
  PATIENT_UPDATED: "PATIENT_UPDATED",
  PATIENT_DELETED: "PATIENT_DELETED",
  APPOINTMENT_CREATED: "APPOINTMENT_CREATED",
  APPOINTMENT_UPDATED: "APPOINTMENT_UPDATED",
  APPOINTMENT_CHECKED_IN: "APPOINTMENT_CHECKED_IN",
  APPOINTMENT_CANCELLED: "APPOINTMENT_CANCELLED",
  APPOINTMENT_RESCHEDULED: "APPOINTMENT_RESCHEDULED",
  TOKEN_ISSUED: "TOKEN_ISSUED",
  TOKEN_CALLED: "TOKEN_CALLED",
  TOKEN_RECALLED: "TOKEN_RECALLED",
  TOKEN_STARTED: "TOKEN_STARTED",
  TOKEN_COMPLETED: "TOKEN_COMPLETED",
  TOKEN_SKIPPED: "TOKEN_SKIPPED",
  TOKEN_CANCELLED: "TOKEN_CANCELLED",
  TOKEN_NO_SHOW: "TOKEN_NO_SHOW",
  TOKEN_REQUEUED: "TOKEN_REQUEUED",
  QUEUE_OPENED: "QUEUE_OPENED",
  QUEUE_PAUSED: "QUEUE_PAUSED",
  QUEUE_RESUMED: "QUEUE_RESUMED",
  QUEUE_CLOSED: "QUEUE_CLOSED",
  QUEUE_RESET: "QUEUE_RESET",
  QUEUE_RECONCILED: "QUEUE_RECONCILED",
  DOCTOR_CREATED: "DOCTOR_CREATED",
  DOCTOR_UPDATED: "DOCTOR_UPDATED",
  DOCTOR_DELETED: "DOCTOR_DELETED",
  CHAMBER_CREATED: "CHAMBER_CREATED",
  CHAMBER_UPDATED: "CHAMBER_UPDATED",
  CHAMBER_DELETED: "CHAMBER_DELETED",
  SCHEDULE_CREATED: "SCHEDULE_CREATED",
  SCHEDULE_UPDATED: "SCHEDULE_UPDATED",
  SCHEDULE_DELETED: "SCHEDULE_DELETED",
  USER_CREATED: "USER_CREATED",
  USER_UPDATED: "USER_UPDATED",
  USER_DISABLED: "USER_DISABLED",
  USER_PASSWORD_RESET: "USER_PASSWORD_RESET",
  CONSULTATION_STARTED: "CONSULTATION_STARTED",
  CONSULTATION_DICTATED: "CONSULTATION_DICTATED",
  CONSULTATION_UPDATED: "CONSULTATION_UPDATED",
  CONSULTATION_SIGNED: "CONSULTATION_SIGNED",
  AUTH_LOGIN_SUCCESS: "AUTH_LOGIN_SUCCESS",
  AUTH_LOGIN_FAILED: "AUTH_LOGIN_FAILED",
  AUTH_LOGOUT: "AUTH_LOGOUT",
  AUTH_PASSWORD_CHANGED: "AUTH_PASSWORD_CHANGED",
  REFRESH_REUSE_DETECTED: "REFRESH_REUSE_DETECTED",
  DISPLAY_SESSION_CREATED: "DISPLAY_SESSION_CREATED",
  DISPLAY_SESSION_REVOKED: "DISPLAY_SESSION_REVOKED",
  CLINIC_SETTINGS_UPDATED: "CLINIC_SETTINGS_UPDATED"
};

// src/error-codes.ts
var ErrorCode = {
  // ── Auth ────────────────────────────────────────────────────────────────
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  ACCOUNT_LOCKED: "ACCOUNT_LOCKED",
  ACCOUNT_INACTIVE: "ACCOUNT_INACTIVE",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  TOKEN_REVOKED: "TOKEN_REVOKED",
  REFRESH_REUSE_DETECTED: "REFRESH_REUSE_DETECTED",
  UNAUTHENTICATED: "UNAUTHENTICATED",
  FORBIDDEN: "FORBIDDEN",
  INSUFFICIENT_ROLE: "INSUFFICIENT_ROLE",
  CROSS_CLINIC_ACCESS: "CROSS_CLINIC_ACCESS",
  PASSWORD_MISMATCH: "PASSWORD_MISMATCH",
  // ── Validation ──────────────────────────────────────────────────────────
  VALIDATION_ERROR: "VALIDATION_ERROR",
  INVALID_OBJECT_ID: "INVALID_OBJECT_ID",
  INVALID_DATE_RANGE: "INVALID_DATE_RANGE",
  INVALID_TIME_FORMAT: "INVALID_TIME_FORMAT",
  // ── Patient ─────────────────────────────────────────────────────────────
  PATIENT_NOT_FOUND: "PATIENT_NOT_FOUND",
  DUPLICATE_MOBILE: "DUPLICATE_MOBILE",
  PATIENT_INACTIVE: "PATIENT_INACTIVE",
  PATIENT_HAS_ACTIVE_TOKEN: "PATIENT_HAS_ACTIVE_TOKEN",
  // ── Doctor / Chamber ────────────────────────────────────────────────────
  DOCTOR_NOT_FOUND: "DOCTOR_NOT_FOUND",
  DOCTOR_INACTIVE: "DOCTOR_INACTIVE",
  DOCTOR_HAS_ACTIVE_QUEUE: "DOCTOR_HAS_ACTIVE_QUEUE",
  TOKEN_PREFIX_TAKEN: "TOKEN_PREFIX_TAKEN",
  CHAMBER_NOT_FOUND: "CHAMBER_NOT_FOUND",
  CHAMBER_ALREADY_ASSIGNED: "CHAMBER_ALREADY_ASSIGNED",
  // ── Schedule ────────────────────────────────────────────────────────────
  SCHEDULE_NOT_FOUND: "SCHEDULE_NOT_FOUND",
  SCHEDULE_OVERLAP: "SCHEDULE_OVERLAP",
  OUTSIDE_SCHEDULE: "OUTSIDE_SCHEDULE",
  SLOT_UNAVAILABLE: "SLOT_UNAVAILABLE",
  MAX_PATIENTS_REACHED: "MAX_PATIENTS_REACHED",
  // ── Appointment ─────────────────────────────────────────────────────────
  APPOINTMENT_NOT_FOUND: "APPOINTMENT_NOT_FOUND",
  APPOINTMENT_ALREADY_CHECKED_IN: "APPOINTMENT_ALREADY_CHECKED_IN",
  APPOINTMENT_CANCELLED: "APPOINTMENT_CANCELLED",
  APPOINTMENT_LOCKED: "APPOINTMENT_LOCKED",
  INVALID_APPOINTMENT_STATUS: "INVALID_APPOINTMENT_STATUS",
  // ── Token / Queue (the critical surface) ────────────────────────────────
  TOKEN_NOT_FOUND: "TOKEN_NOT_FOUND",
  INVALID_TOKEN_TRANSITION: "INVALID_TOKEN_TRANSITION",
  TOKEN_ALREADY_PROCESSED: "TOKEN_ALREADY_PROCESSED",
  NO_WAITING_PATIENT: "NO_WAITING_PATIENT",
  ACTIVE_TOKEN_EXISTS: "ACTIVE_TOKEN_EXISTS",
  RECALL_LIMIT_REACHED: "RECALL_LIMIT_REACHED",
  QUEUE_NOT_FOUND: "QUEUE_NOT_FOUND",
  QUEUE_NOT_OPEN: "QUEUE_NOT_OPEN",
  QUEUE_CLOSED: "QUEUE_CLOSED",
  SEQUENCE_ALLOCATION_FAILED: "SEQUENCE_ALLOCATION_FAILED",
  // ── Consultation / dictation ────────────────────────────────────────────
  CONSULTATION_NOT_FOUND: "CONSULTATION_NOT_FOUND",
  CONSULTATION_ALREADY_SIGNED: "CONSULTATION_ALREADY_SIGNED",
  CONSULTATION_NOT_STARTED: "CONSULTATION_NOT_STARTED",
  DICTATION_DISABLED: "DICTATION_DISABLED",
  TRANSCRIPTION_FAILED: "TRANSCRIPTION_FAILED",
  STRUCTURING_FAILED: "STRUCTURING_FAILED",
  AUDIO_TOO_LARGE: "AUDIO_TOO_LARGE",
  UNSUPPORTED_AUDIO_FORMAT: "UNSUPPORTED_AUDIO_FORMAT",
  EMPTY_TRANSCRIPT: "EMPTY_TRANSCRIPT",
  // ── Display ─────────────────────────────────────────────────────────────
  DISPLAY_SESSION_NOT_FOUND: "DISPLAY_SESSION_NOT_FOUND",
  DISPLAY_SESSION_REVOKED: "DISPLAY_SESSION_REVOKED",
  FORBIDDEN_QUEUE: "FORBIDDEN_QUEUE",
  // ── Clinic / User ───────────────────────────────────────────────────────
  CLINIC_NOT_FOUND: "CLINIC_NOT_FOUND",
  USER_NOT_FOUND: "USER_NOT_FOUND",
  EMAIL_TAKEN: "EMAIL_TAKEN",
  // ── Generic ─────────────────────────────────────────────────────────────
  NOT_FOUND: "NOT_FOUND",
  CONFLICT: "CONFLICT",
  DUPLICATE_KEY: "DUPLICATE_KEY",
  RATE_LIMITED: "RATE_LIMITED",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  SERVICE_UNAVAILABLE: "SERVICE_UNAVAILABLE"
};
var ERROR_MESSAGES = {
  INVALID_CREDENTIALS: "Invalid email or password",
  ACCOUNT_LOCKED: "Account temporarily locked after too many failed attempts",
  ACCOUNT_INACTIVE: "This account is not active",
  TOKEN_EXPIRED: "Session expired, please sign in again",
  TOKEN_REVOKED: "Session has been revoked",
  REFRESH_REUSE_DETECTED: "Session security violation detected, please sign in again",
  UNAUTHENTICATED: "Authentication required",
  FORBIDDEN: "You do not have permission to perform this action",
  INSUFFICIENT_ROLE: "Your role does not permit this action",
  CROSS_CLINIC_ACCESS: "Resource belongs to a different clinic",
  PASSWORD_MISMATCH: "Current password is incorrect",
  VALIDATION_ERROR: "Validation failed",
  INVALID_OBJECT_ID: "Malformed identifier",
  INVALID_DATE_RANGE: "Invalid date range",
  INVALID_TIME_FORMAT: "Time must be in HH:mm format",
  PATIENT_NOT_FOUND: "Patient not found",
  DUPLICATE_MOBILE: "A patient with this mobile number already exists",
  PATIENT_INACTIVE: "This patient record is inactive",
  PATIENT_HAS_ACTIVE_TOKEN: "Patient has an active token in a queue",
  DOCTOR_NOT_FOUND: "Doctor not found",
  DOCTOR_INACTIVE: "This doctor is not active",
  DOCTOR_HAS_ACTIVE_QUEUE: "Doctor has an active queue with waiting patients",
  TOKEN_PREFIX_TAKEN: "Another doctor already uses this token prefix",
  CHAMBER_NOT_FOUND: "Chamber not found",
  CHAMBER_ALREADY_ASSIGNED: "Chamber is already assigned to another doctor",
  SCHEDULE_NOT_FOUND: "Schedule not found",
  SCHEDULE_OVERLAP: "This schedule overlaps an existing one",
  OUTSIDE_SCHEDULE: "Requested time falls outside the doctor schedule",
  SLOT_UNAVAILABLE: "This time slot is already booked",
  MAX_PATIENTS_REACHED: "Maximum patients for this session reached",
  APPOINTMENT_NOT_FOUND: "Appointment not found",
  APPOINTMENT_ALREADY_CHECKED_IN: "Appointment is already checked in",
  APPOINTMENT_CANCELLED: "Appointment has been cancelled",
  APPOINTMENT_LOCKED: "Appointment can no longer be edited",
  INVALID_APPOINTMENT_STATUS: "Invalid appointment status for this operation",
  TOKEN_NOT_FOUND: "Token not found",
  INVALID_TOKEN_TRANSITION: "This action is not valid for the token in its current state",
  TOKEN_ALREADY_PROCESSED: "Token is already being processed",
  NO_WAITING_PATIENT: "No waiting patient found",
  ACTIVE_TOKEN_EXISTS: "Finish the current patient before calling the next one",
  RECALL_LIMIT_REACHED: "Recall limit reached for this token",
  QUEUE_NOT_FOUND: "Queue not found",
  QUEUE_NOT_OPEN: "Queue is not open",
  QUEUE_CLOSED: "Queue is closed for the day",
  SEQUENCE_ALLOCATION_FAILED: "Could not allocate a token number, please retry",
  CONSULTATION_NOT_FOUND: "Consultation not found",
  CONSULTATION_ALREADY_SIGNED: "This consultation has been signed and can no longer be edited",
  CONSULTATION_NOT_STARTED: "Start the consultation before writing the prescription",
  DICTATION_DISABLED: "Voice dictation is not configured on this server",
  TRANSCRIPTION_FAILED: "Could not transcribe the recording, please try again",
  STRUCTURING_FAILED: "Transcribed the recording but could not structure it into the Rx pad",
  AUDIO_TOO_LARGE: "Recording is too long, please dictate in shorter passes",
  UNSUPPORTED_AUDIO_FORMAT: "Unsupported audio format",
  EMPTY_TRANSCRIPT: "Nothing audible was recorded",
  DISPLAY_SESSION_NOT_FOUND: "Display session not found",
  DISPLAY_SESSION_REVOKED: "This display has been revoked",
  FORBIDDEN_QUEUE: "This display is not permitted to view that queue",
  CLINIC_NOT_FOUND: "Clinic not found",
  USER_NOT_FOUND: "User not found",
  EMAIL_TAKEN: "This email address is already registered",
  NOT_FOUND: "Resource not found",
  CONFLICT: "Request conflicts with the current state",
  DUPLICATE_KEY: "A record with these details already exists",
  RATE_LIMITED: "Too many requests, please slow down",
  INTERNAL_ERROR: "Something went wrong on our side",
  SERVICE_UNAVAILABLE: "Service temporarily unavailable"
};

// src/token-actions.ts
var TOKEN_ACTIONS = [
  "call",
  "recall",
  "start",
  "complete",
  "skip",
  "noShow",
  "cancel",
  "requeue"
];
var TRANSITION_RULES = {
  call: {
    from: [TokenStatus.WAITING],
    to: TokenStatus.CALLED,
    roles: [UserRole.ADMIN, UserRole.RECEPTIONIST, UserRole.DOCTOR]
  },
  // Self-loop: re-announces without changing state, so `calledAt` (and therefore
  // the waiting-time metric) is never reset.
  recall: {
    from: [TokenStatus.CALLED],
    to: TokenStatus.CALLED,
    roles: [UserRole.ADMIN, UserRole.RECEPTIONIST, UserRole.DOCTOR]
  },
  start: {
    from: [TokenStatus.CALLED],
    to: TokenStatus.IN_CONSULTATION,
    roles: [UserRole.ADMIN, UserRole.DOCTOR]
  },
  complete: {
    from: [TokenStatus.IN_CONSULTATION],
    to: TokenStatus.COMPLETED,
    roles: [UserRole.ADMIN, UserRole.DOCTOR]
  },
  skip: {
    from: [TokenStatus.WAITING, TokenStatus.CALLED],
    to: TokenStatus.SKIPPED,
    roles: [UserRole.ADMIN, UserRole.RECEPTIONIST, UserRole.DOCTOR]
  },
  noShow: {
    from: [TokenStatus.CALLED],
    to: TokenStatus.NO_SHOW,
    roles: [UserRole.ADMIN, UserRole.RECEPTIONIST, UserRole.DOCTOR]
  },
  cancel: {
    from: [TokenStatus.WAITING, TokenStatus.CALLED, TokenStatus.IN_CONSULTATION],
    to: TokenStatus.CANCELLED,
    roles: [UserRole.ADMIN, UserRole.RECEPTIONIST],
    requiresReason: true
  },
  requeue: {
    from: [TokenStatus.SKIPPED, TokenStatus.NO_SHOW],
    to: TokenStatus.WAITING,
    roles: [UserRole.ADMIN, UserRole.RECEPTIONIST, UserRole.DOCTOR]
  }
};
var TERMINAL_TOKEN_STATUSES = [
  TokenStatus.COMPLETED,
  TokenStatus.CANCELLED
];
var ACTIVE_TOKEN_STATUSES = [
  TokenStatus.CALLED,
  TokenStatus.IN_CONSULTATION
];
var LIVE_TOKEN_STATUSES = [
  TokenStatus.WAITING,
  TokenStatus.CALLED,
  TokenStatus.IN_CONSULTATION
];
var TOKEN_ACTION_LABELS = {
  call: "Call",
  recall: "Recall",
  start: "Start Consultation",
  complete: "Complete",
  skip: "Skip",
  noShow: "No Show",
  cancel: "Cancel",
  requeue: "Requeue"
};
function canTransition(from, action) {
  return TRANSITION_RULES[action].from.includes(from);
}
function isRoleAllowed(role, action) {
  return TRANSITION_RULES[action].roles.includes(role);
}
function availableActions(from, role) {
  return TOKEN_ACTIONS.filter((a) => canTransition(from, a) && isRoleAllowed(role, a));
}

// src/consultation-contracts.ts
var EMPTY_VITALS = {
  pulseBpm: null,
  bloodPressure: null,
  temperature: null,
  spo2: null,
  respiratoryRate: null,
  weight: null,
  height: null,
  bloodSugar: null
};
var EMPTY_RX_PAD = {
  symptoms: [],
  examination: [],
  diagnosis: [],
  medications: [],
  investigations: [],
  advice: [],
  vitals: EMPTY_VITALS,
  followUp: null,
  summary: null
};
var RX_VITAL_FIELDS = [
  "pulseBpm",
  "bloodPressure",
  "temperature",
  "spo2",
  "respiratoryRate",
  "weight",
  "height",
  "bloodSugar"
];
var RX_LINE_SECTIONS = [
  "symptoms",
  "examination",
  "diagnosis",
  "investigations",
  "advice"
];

// src/socket-contracts.ts
var SOCKET_NAMESPACE = "/realtime";
var QueueEvent = {
  SNAPSHOT: "queue.snapshot",
  UPDATED: "queue.updated",
  TOKEN_ISSUED: "token.issued",
  TOKEN_CALLED: "token.called",
  TOKEN_RECALLED: "token.recalled",
  TOKEN_STARTED: "token.started",
  TOKEN_COMPLETED: "token.completed",
  TOKEN_SKIPPED: "token.skipped",
  TOKEN_CANCELLED: "token.cancelled",
  TOKEN_NO_SHOW: "token.noShow",
  TOKEN_REQUEUED: "token.requeued",
  QUEUE_OPENED: "queue.opened",
  QUEUE_PAUSED: "queue.paused",
  QUEUE_RESUMED: "queue.resumed",
  QUEUE_CLOSED: "queue.closed",
  QUEUE_RESET: "queue.reset"
};
var QUEUE_EVENTS = Object.values(QueueEvent);
var SystemEvent = {
  DISPLAY_CONNECTED: "display.connected",
  DISPLAY_DISCONNECTED: "display.disconnected",
  CHAMBER_DOCTOR_CHANGED: "chamber.doctorChanged",
  NOTIFICATION_CREATED: "notification.created",
  AUTH_EXPIRING: "auth.expiring",
  ERROR: "error"
};
var PUBLIC_META_KEYS = [
  "tokenNumber",
  "previousTokenNumber",
  "announce",
  "recallCount",
  "consultationTimeSec"
];
var ClientEvent = {
  SUBSCRIBE: "queue.subscribe",
  UNSUBSCRIBE: "queue.unsubscribe",
  REQUEST_SNAPSHOT: "queue.requestSnapshot",
  DISPLAY_HEARTBEAT: "display.heartbeat"
};
var Rooms = {
  clinic: (clinicId) => `clinic:${clinicId}`,
  staff: (clinicId, doctorId) => `staff:${clinicId}:${doctorId}`,
  display: (clinicId, doctorId) => `display:${clinicId}:${doctorId}`,
  chamber: (clinicId, chamberId) => `chamber:${clinicId}:${chamberId}`,
  user: (userId) => `user:${userId}`
};

// src/utils.ts
function formatTokenNumber(prefix, sequence, padding = 3) {
  return `${prefix.toUpperCase()}-${String(sequence).padStart(padding, "0")}`;
}
function formatPatientCode(seq) {
  return `PAT-${String(seq).padStart(6, "0")}`;
}
function formatDoctorCode(seq) {
  return `DOC-${String(seq).padStart(3, "0")}`;
}
function formatChamberCode(seq) {
  return `CH-${String(seq).padStart(3, "0")}`;
}
function formatAppointmentNumber(queueDate, seq) {
  return `APT-${queueDate.replace(/-/g, "")}-${String(seq).padStart(4, "0")}`;
}
function normalizeSearchTerm(value) {
  return value.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase().replace(/\s+/g, " ").trim();
}
function escapeRegExp(value) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function initialsOf(fullName) {
  return fullName.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => `${part[0].toUpperCase()}.`).join("");
}
function timeToMinutes(time) {
  const [h, m] = time.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}
function minutesToTime(minutes) {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}
function isValidTime(value) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}
function isValidQueueDate(value) {
  return /^\d{4}-\d{2}-\d{2}$/.test(value) && !Number.isNaN(Date.parse(value));
}
function isValidObjectId(value) {
  return /^[0-9a-fA-F]{24}$/.test(value);
}
function isValidIndianMobile(value) {
  return /^[6-9]\d{9}$/.test(value);
}
function formatDuration(seconds) {
  if (seconds === null || seconds === void 0 || Number.isNaN(seconds)) return "\u2014";
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor(s % 3600 / 60);
  const rem = s % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m > 0) return `${m}m ${String(rem).padStart(2, "0")}s`;
  return `${rem}s`;
}
function ageFromDateOfBirth(dob) {
  const birth = typeof dob === "string" ? new Date(dob) : dob;
  const now = /* @__PURE__ */ new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  if (monthDiff < 0 || monthDiff === 0 && now.getDate() < birth.getDate()) age -= 1;
  return Math.max(0, age);
}
var TOKEN_STATUS_PRESENTATION = {
  WAITING: {
    label: "Waiting",
    className: "bg-amber-100 text-amber-900 border-amber-300",
    icon: "Clock"
  },
  CALLED: {
    label: "Called",
    className: "bg-blue-100 text-blue-900 border-blue-300",
    icon: "BellRing"
  },
  IN_CONSULTATION: {
    label: "In Consultation",
    className: "bg-violet-100 text-violet-900 border-violet-300",
    icon: "Stethoscope"
  },
  COMPLETED: {
    label: "Completed",
    className: "bg-emerald-100 text-emerald-900 border-emerald-300",
    icon: "CheckCircle2"
  },
  SKIPPED: {
    label: "Skipped",
    className: "bg-orange-100 text-orange-900 border-orange-300",
    icon: "SkipForward"
  },
  CANCELLED: {
    label: "Cancelled",
    className: "bg-slate-200 text-slate-800 border-slate-400",
    icon: "XCircle"
  },
  NO_SHOW: {
    label: "No Show",
    className: "bg-rose-100 text-rose-900 border-rose-300",
    icon: "UserX"
  }
};
var APPOINTMENT_STATUS_PRESENTATION = {
  SCHEDULED: {
    label: "Scheduled",
    className: "bg-sky-100 text-sky-900 border-sky-300",
    icon: "CalendarClock"
  },
  CHECKED_IN: {
    label: "Checked In",
    className: "bg-teal-100 text-teal-900 border-teal-300",
    icon: "LogIn"
  },
  // Statuses an appointment shares with its token mirror the token presentation
  // exactly, so a badge can never mean two different things on two screens.
  WAITING: TOKEN_STATUS_PRESENTATION.WAITING,
  CALLED: TOKEN_STATUS_PRESENTATION.CALLED,
  IN_CONSULTATION: TOKEN_STATUS_PRESENTATION.IN_CONSULTATION,
  COMPLETED: TOKEN_STATUS_PRESENTATION.COMPLETED,
  CANCELLED: TOKEN_STATUS_PRESENTATION.CANCELLED,
  NO_SHOW: TOKEN_STATUS_PRESENTATION.NO_SHOW,
  SKIPPED: TOKEN_STATUS_PRESENTATION.SKIPPED
};

// src/validation.ts
import { z } from "zod";
var objectIdSchema = z.string().regex(/^[0-9a-fA-F]{24}$/, "Invalid identifier");
var mobileSchema = z.string().regex(/^[6-9]\d{9}$/, "Enter a valid 10-digit Indian mobile number");
var timeSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Time must be in HH:mm format");
var queueDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Date must be in YYYY-MM-DD format");
var usernameSchema = z.string().trim().toLowerCase().min(3, "Username must be at least 3 characters").max(30, "Username must be at most 30 characters").regex(
  /^[a-z0-9][a-z0-9._-]*$/,
  "Use lowercase letters, numbers, dots, underscores and hyphens"
);
var loginSchema = z.object({
  username: z.string().min(1, "Username is required"),
  password: z.string().min(1, "Password is required")
});
var PIN_LENGTH = 6;
var pinSchema = z.string().regex(new RegExp(`^\\d{${PIN_LENGTH}}$`), `PIN must be exactly ${PIN_LENGTH} digits`).refine((pin) => !isGuessablePin(pin), "Choose a less predictable PIN");
function isGuessablePin(pin) {
  if (/^(\d)\1*$/.test(pin)) return true;
  const digits = [...pin].map(Number);
  const ascending = digits.every((digit, i) => i === 0 || digit === digits[i - 1] + 1);
  const descending = digits.every((digit, i) => i === 0 || digit === digits[i - 1] - 1);
  return ascending || descending;
}
var screenLoginSchema = z.object({
  username: z.string().min(1, "Screen username is required"),
  password: z.string().min(1, "Password is required")
});
var chamberLoginSchema = z.object({
  doctorId: z.string().min(1, "Select your name"),
  pin: pinSchema
});
var passwordSchema = z.string().min(8, "Password must be at least 8 characters").regex(/[a-z]/, "Must contain a lowercase letter").regex(/[A-Z]/, "Must contain an uppercase letter").regex(/\d/, "Must contain a number");
var changePasswordSchema = z.object({
  currentPassword: z.string().min(1, "Current password is required"),
  newPassword: passwordSchema,
  confirmPassword: z.string()
}).refine((d) => d.newPassword === d.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});
var patientSchema = z.object({
  fullName: z.string().min(2, "Name must be at least 2 characters").max(120),
  mobile: mobileSchema,
  alternateMobile: z.union([mobileSchema, z.literal("")]).optional(),
  email: z.union([z.string().email("Enter a valid email address"), z.literal("")]).optional(),
  gender: z.enum(GENDERS),
  dateOfBirth: z.string().optional(),
  ageYears: z.coerce.number().int().min(0).max(130).optional(),
  address: z.string().max(300).optional(),
  city: z.string().max(80).optional(),
  pincode: z.union([z.string().regex(/^\d{6}$/, "Pincode must be 6 digits"), z.literal("")]).optional(),
  bloodGroup: z.string().max(5).optional(),
  emergencyContactName: z.string().max(120).optional(),
  emergencyContactRelation: z.string().max(60).optional(),
  emergencyContactMobile: z.union([mobileSchema, z.literal("")]).optional(),
  notes: z.string().max(2e3).optional(),
  allowDuplicateMobile: z.boolean().optional()
}).refine((d) => Boolean(d.dateOfBirth) || typeof d.ageYears === "number", {
  message: "Provide either a date of birth or an age",
  path: ["ageYears"]
});
var doctorSchema = z.object({
  fullName: z.string().min(2).max(120),
  specialization: z.string().min(2).max(120),
  tokenPrefix: z.string().regex(/^[A-Za-z]{1,3}$/, "Prefix must be 1\u20133 letters").transform((v) => v.toUpperCase()),
  consultationDuration: z.coerce.number().int().min(1).max(240),
  phone: z.union([mobileSchema, z.literal("")]).optional(),
  email: z.union([z.string().email(), z.literal("")]).optional(),
  qualification: z.string().max(200).optional(),
  registrationNumber: z.string().max(60).optional(),
  department: z.string().max(80).optional(),
  chamberId: z.union([objectIdSchema, z.literal("")]).optional(),
  bio: z.string().max(1e3).optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional()
});
var chamberSchema = z.object({
  name: z.string().min(1).max(80),
  roomNumber: z.string().min(1).max(20),
  displayName: z.string().min(1).max(80),
  floor: z.string().max(20).optional(),
  doctorId: z.union([objectIdSchema, z.literal("")]).optional(),
  status: z.enum(["ACTIVE", "INACTIVE", "MAINTENANCE"]).optional()
});
var screenCredentialsSchema = z.object({
  username: usernameSchema,
  password: passwordSchema,
  /** Sign out screens already signed in to this room — use when rotating a leaked password. */
  signOutExistingScreens: z.boolean().optional()
});
var userSchema = z.object({
  fullName: z.string().min(2).max(120),
  username: usernameSchema,
  email: z.string().email(),
  password: passwordSchema.optional(),
  role: z.enum(USER_ROLES),
  doctorId: z.union([objectIdSchema, z.literal("")]).optional(),
  phone: z.union([mobileSchema, z.literal("")]).optional(),
  status: z.enum(["ACTIVE", "INACTIVE", "LOCKED"]).optional()
});
var scheduleSchema = z.object({
  doctorId: objectIdSchema,
  dayOfWeek: z.coerce.number().int().min(0).max(6),
  startTime: timeSchema,
  endTime: timeSchema,
  breakStart: z.union([timeSchema, z.literal("")]).optional(),
  breakEnd: z.union([timeSchema, z.literal("")]).optional(),
  slotDuration: z.coerce.number().int().min(1).max(240),
  maxPatients: z.coerce.number().int().min(1).max(500),
  chamberId: z.union([objectIdSchema, z.literal("")]).optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional()
}).refine((d) => d.startTime < d.endTime, {
  message: "End time must be after start time",
  path: ["endTime"]
}).refine((d) => Boolean(d.breakStart) === Boolean(d.breakEnd), {
  message: "Provide both break start and break end",
  path: ["breakEnd"]
}).refine((d) => !d.breakStart || !d.breakEnd || d.breakStart < d.breakEnd, {
  message: "Break end must be after break start",
  path: ["breakEnd"]
});
var appointmentSchema = z.object({
  patientId: objectIdSchema,
  doctorId: objectIdSchema,
  appointmentDate: queueDateSchema,
  appointmentTime: z.union([timeSchema, z.literal("")]).optional(),
  appointmentType: z.enum(APPOINTMENT_TYPES),
  reason: z.string().max(500).optional(),
  notes: z.string().max(2e3).optional(),
  override: z.boolean().optional()
});
var quickWalkInSchema = z.object({
  mode: z.enum(["existing", "new"]),
  patientId: z.union([objectIdSchema, z.literal("")]).optional(),
  fullName: z.string().max(120).optional(),
  mobile: z.union([mobileSchema, z.literal("")]).optional(),
  gender: z.enum(GENDERS).optional(),
  ageYears: z.coerce.number().int().min(0).max(130).optional(),
  doctorId: objectIdSchema,
  appointmentType: z.enum(APPOINTMENT_TYPES),
  reason: z.string().max(500).optional(),
  allowDuplicateMobile: z.boolean().optional()
});
var cancelReasonSchema = z.object({
  reason: z.string().min(3, "Please give a short reason").max(300)
});
var displaySessionSchema = z.object({
  name: z.string().min(2).max(80),
  doctorId: z.union([objectIdSchema, z.literal("")]).optional(),
  chamberId: z.union([objectIdSchema, z.literal("")]).optional()
});
export {
  ACTIVE_TOKEN_STATUSES,
  APPOINTMENT_STATUSES,
  APPOINTMENT_STATUS_PRESENTATION,
  APPOINTMENT_TYPES,
  AppointmentStatus,
  AppointmentType,
  AuditAction,
  CONSULTATION_STATUSES,
  CallNextPolicy,
  ChamberStatus,
  ClientEvent,
  ConsultationStatus,
  DICTATION_MODES,
  DictationMode,
  EMERGENCY_PRIORITY,
  EMPTY_RX_PAD,
  EMPTY_VITALS,
  ERROR_MESSAGES,
  EntityStatus,
  ErrorCode,
  GENDERS,
  Gender,
  LIVE_TOKEN_STATUSES,
  NORMAL_PRIORITY,
  NotificationChannel,
  NotificationStatus,
  PIN_LENGTH,
  PUBLIC_META_KEYS,
  PatientStatus,
  QUEUE_EVENTS,
  QUEUE_STATUSES,
  QueueEvent,
  QueueStatus,
  RX_LINE_SECTIONS,
  RX_VITAL_FIELDS,
  Rooms,
  RxSource,
  SOCKET_NAMESPACE,
  SystemEvent,
  TERMINAL_TOKEN_STATUSES,
  TOKEN_ACTIONS,
  TOKEN_ACTION_LABELS,
  TOKEN_STATUSES,
  TOKEN_STATUS_PRESENTATION,
  TRANSITION_RULES,
  TokenStatus,
  USER_ROLES,
  UserRole,
  UserStatus,
  ageFromDateOfBirth,
  appointmentSchema,
  availableActions,
  canTransition,
  cancelReasonSchema,
  chamberLoginSchema,
  chamberSchema,
  changePasswordSchema,
  displaySessionSchema,
  doctorSchema,
  escapeRegExp,
  formatAppointmentNumber,
  formatChamberCode,
  formatDoctorCode,
  formatDuration,
  formatPatientCode,
  formatTokenNumber,
  initialsOf,
  isGuessablePin,
  isRoleAllowed,
  isValidIndianMobile,
  isValidObjectId,
  isValidQueueDate,
  isValidTime,
  loginSchema,
  minutesToTime,
  mobileSchema,
  normalizeSearchTerm,
  objectIdSchema,
  passwordSchema,
  patientSchema,
  pinSchema,
  queueDateSchema,
  quickWalkInSchema,
  scheduleSchema,
  screenCredentialsSchema,
  screenLoginSchema,
  timeSchema,
  timeToMinutes,
  userSchema,
  usernameSchema
};
//# sourceMappingURL=index.mjs.map